import { useState, FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/d71ig3rk1jkj467r5h20";

export default function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="subscribe"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #451a03 0%, #78350f 45%, #92400e 100%)" }}
    >
      {/* Soft decorative glows */}
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-amber-600/10 -translate-y-1/3 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-amber-500/8 translate-y-1/3 -translate-x-1/3" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[600px] rounded-full bg-amber-700/5" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 lg:px-12 text-center">

        {/* Icon */}
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-400/30 mx-auto mb-8">
          <i className="ri-notification-3-line text-amber-300 text-2xl" />
        </div>

        {/* Heading */}
        <h2 className="font-serif text-4xl lg:text-5xl font-bold leading-tight mb-5" style={{ color: "#fef9ee" }}>
          Stay Curious.<br />
          <em className="text-amber-400">Stay Informed.</em>
        </h2>

        {/* Subtext */}
        <p className="font-sans text-base leading-relaxed mb-10 max-w-lg mx-auto" style={{ color: "rgba(253,230,138,0.70)" }}>
          New episodes drop regularly. Subscribe to get notified when the next conversation is ready — and join a community of people who believe the future belongs to the informed.
        </p>

        {/* Form */}
        {status === "success" ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-400/20 border border-amber-400/30">
              <i className="ri-check-line text-amber-300 text-3xl" />
            </div>
            <p className="font-serif text-2xl" style={{ color: "#fef9ee" }}>
              Welcome to the community!
            </p>
            <p className="font-sans text-sm" style={{ color: "rgba(253,230,138,0.65)" }}>
              Check your inbox — we&apos;ll be in touch when the next episode drops.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 font-sans text-sm bg-white/10 border border-amber-500/30 rounded-full px-6 py-4 focus:outline-none focus:border-amber-400 focus:bg-white/15 transition-all"
              style={{ color: "#fef9ee" }}
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="font-sans font-bold text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {status === "loading" ? "Subscribing..." : "Subscribe"}
            </button>
          </form>
        )}

        {status === "error" && (
          <p className="font-sans text-sm text-red-300 mt-3">
            Something went wrong — please try again.
          </p>
        )}

        {/* Reassurance line */}
        {status !== "success" && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2" style={{ color: "rgba(253,230,138,0.50)" }}>
              <i className="ri-lock-line text-amber-500 text-sm" />
              <span className="font-sans text-xs">No spam, ever</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: "rgba(253,230,138,0.50)" }}>
              <i className="ri-arrow-go-back-line text-amber-500 text-sm" />
              <span className="font-sans text-xs">Unsubscribe anytime</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: "rgba(253,230,138,0.50)" }}>
              <i className="ri-heart-line text-amber-500 text-sm" />
              <span className="font-sans text-xs">Always free</span>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
